import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Layout, LayoutWidget } from '../../redux/config/layout';
import dragula from 'dragula';
import type { DragulaLike } from '../../types/dragula-like';
import { useDispatch } from 'react-redux';
import { getWidgetLayoutIds, updateDataPosition } from './layout-tab.functions';
import { addWidgetsReorder } from '../../redux/slices/widgetChart/WidgetChartSlice';
import { useUpdateLayoutWidgetMutation } from '../../redux/config/layout-widget';
import { useEditSaveDiscard } from '../Charts/hooks/useSaveEdit';
import { TabWidget } from './tab-widget.component';
import { resetChildrenPosition } from '../../helpers/reset-children-position';

type TabElementProps = {
  layout: Layout;
  assignChildDraggableContainer: (
    editEnabled: boolean,
    drakeRef: React.MutableRefObject<DragulaLike | undefined>,
    childContainer: HTMLElement | null
  ) => void;
  isEditable: boolean;
};

export const TabContent = ({ layout, assignChildDraggableContainer, isEditable }: TabElementProps) => {
  const dispatch = useDispatch();
  const [updateLayoutWidget] = useUpdateLayoutWidgetMutation();
  const tabId = useRef<string>(`mainWidgetCard-${layout.id}`);
  // Using the type from @types/dragula. The default export returns a DragulaDrake drake instance.
  const drakeWidgetRef = useRef<DragulaLike>();
  const [currentTabRef, setCurrentTabRef] = useState<HTMLDivElement>();
  const saveChangesEdit = useEditSaveDiscard();
  const widgetsReorderd = useRef(false);

  useEffect(() => {
    if (saveChangesEdit !== undefined && currentTabRef && widgetsReorderd.current) {
      if (saveChangesEdit) {
        const widgetIds = getWidgetLayoutIds(currentTabRef.children);
        updateLayoutWidget(widgetIds);
        updateDataPosition(currentTabRef.children);
      } else {
        resetChildrenPosition(currentTabRef);
      }
    }
  }, [saveChangesEdit, currentTabRef, updateLayoutWidget]);

  useEffect(() => {
    if (currentTabRef) {
      assignChildDraggableContainer(isEditable, drakeWidgetRef, currentTabRef);
    }
  }, [currentTabRef, isEditable, assignChildDraggableContainer]);

  useEffect(() => {
    drakeWidgetRef.current = dragula([], {
      direction: 'vertical',
    });

    drakeWidgetRef.current?.on('drop', (element: Element, target: Element) => {
      const widgetIds = getWidgetLayoutIds(target.children);
      widgetsReorderd.current = true;

      if (widgetIds) {
        dispatch(addWidgetsReorder(widgetIds));
      }
    });

    // Wheel events are registered in drag mode on the mirrored element.
    // Once the cloned element is appended to the DOM we listen for wheel events
    // and modify the parent scroll position. We formerly used a module augmentation
    // for the 'cloned' event; now we rely on the permissive DragulaLike.on signature.
    drakeWidgetRef.current?.on('cloned', (clone: Element) => {
      const parentElement = document.getElementById(tabId.current)?.parentElement;
      if (parentElement) {
        (clone as HTMLDivElement).addEventListener('wheel', (e) => {
          parentElement.scrollBy({
            top: e.deltaY,
          });
        });
      }
    });

    return () => {
      drakeWidgetRef.current?.destroy();
    };
  }, [dispatch]);

  const sortByPosition = useCallback((arr: LayoutWidget[]) => {
    return Array.from(arr).sort((a, b) => a.position - b.position);
  }, []);

  return (
    <div
      className="flex h-full flex-wrap"
      data-tab={layout.title}
      id={tabId.current}
      key={layout.id}
      ref={(el) => el && setCurrentTabRef(el)}
    >
      {sortByPosition(layout.layoutWidgets)?.map((layoutWidget) => (
        <TabWidget layoutWidget={layoutWidget} layout={layout} key={layoutWidget.id} />
      ))}
    </div>
  );
};
